import type { ComponentType, ReactNode } from "react";
import {
  Banknote,
  CalendarDays,
  Clock3,
  CreditCard,
  Mail,
  Phone,
  TicketCheck,
  UserRound,
  Users,
} from "lucide-react";
import { Badge } from "@mantine/core";
import { formatBookingAmount, formatDate, formatMad } from "../helpers/general";

export function bookingAmountMad(booking: Booking) {
  return Number(
    booking.amountMad ?? booking.priceToPay ?? booking.priceToPayInternal ?? 0,
  );
}

export function bookingCommissionMad(booking: Booking) {
  if (booking.commissionAmount != null) {
    return Number(booking.commissionAmount);
  }
  return (
    (bookingAmountMad(booking) * Number(booking.placeCommission ?? 0)) / 100
  );
}

export function bookingDueToVenez(booking: Booking) {
  return Math.max(0, bookingAmountMad(booking) - bookingCommissionMad(booking));
}

export function bookingCustomer(booking: Booking) {
  return {
    fullName:
      booking.bookedForUser?.fullName ||
      booking.user?.fullName ||
      "Client Venez",
    email: booking.bookedForUser?.email || booking.user?.email || "—",
    phone: booking.bookedForUser?.phoneNumber || booking.user?.phone || "—",
  };
}

export function paymentMethodLabel(method?: string) {
  switch (method) {
    case "credit_card":
      return "Carte bancaire (CMI)";
    case "stripe":
      return "Carte bancaire (Stripe)";
    case "wafacash":
      return "Wafacash";
    case "in_place":
      return "Espèces (déjà encaissé)";
    case "user_balance":
      return "Solde Venez";
    default:
      return "Non renseigné";
  }
}

export function bookingStatusLabel(value = "") {
  const normalized = value.toLowerCase();
  if (normalized === "confirmed") return "Confirmée";
  if (["canceled", "cancelled", "refused"].includes(normalized))
    return "Annulée";
  return "En attente";
}

export function BookingStatusBadge({ value = "" }: { value?: string }) {
  const normalized = value.toLowerCase();
  const confirmed = normalized === "confirmed";
  const canceled = ["canceled", "cancelled", "refused"].includes(normalized);
  return (
    <Badge
      variant="outline"
      className={
        canceled
          ? "border-red-200 bg-red-50 text-red-700"
          : confirmed
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-amber-200 bg-amber-50 text-amber-700"
      }
    >
      {bookingStatusLabel(value)}
    </Badge>
  );
}

export function BookingDetail({ booking }: { booking: Booking }) {
  const customer = bookingCustomer(booking);
  const commission = bookingCommissionMad(booking);
  const dueToVenez = bookingDueToVenez(booking);
  const cashCollected = booking.paymentMethod === "in_place";
  const variants = booking.variantsBookings?.filter(
    (item) => item.variant?.description,
  );

  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-extrabold leading-snug">
            {booking.special?.title ||
              booking.event?.name ||
              booking.activity?.title ||
              booking.activityOption?.title ||
              "Activité Venez"}
          </h2>
          <BookingStatusBadge value={booking.status?.label} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Référence {booking.reference}
        </p>
      </div>

      <DetailSection title="Client">
        <DetailRow icon={UserRound} label="Nom" value={customer.fullName} />
        <DetailRow icon={Mail} label="Email" value={customer.email} />
        <DetailRow icon={Phone} label="Téléphone" value={customer.phone} />
      </DetailSection>

      <DetailSection title="Réservation">
        <DetailRow
          icon={CalendarDays}
          label="Date"
          value={formatDate(booking.date)!}
        />
        <DetailRow icon={Clock3} label="Heure" value={booking.hour || "—"} />
        <DetailRow
          icon={Users}
          label="Participants"
          value={String(booking.personsNumber ?? 1)}
        />
        {variants?.map((item) => (
          <DetailRow
            key={item.variant?.description}
            icon={TicketCheck}
            label="Formule"
            value={`${item.variant?.description} × ${item.numberOfReservation ?? 1}`}
          />
        ))}
      </DetailSection>

      <DetailSection title="Paiement">
        <DetailRow
          icon={booking.paymentMethod === "in_place" ? Banknote : CreditCard}
          label="Moyen de paiement"
          value={paymentMethodLabel(booking.paymentMethod)}
        />
        {booking.wafacashCode && (
          <DetailRow
            icon={TicketCheck}
            label="Code Wafacash"
            value={booking.wafacashCode}
          />
        )}
        <DetailRow label="Montant" value={formatBookingAmount(booking)} />
        <DetailRow
          label="Votre commission"
          value={`+ ${formatMad(commission)}`}
          accent
        />
        {cashCollected && (
          <DetailRow label="À régler à Venez" value={formatMad(dueToVenez)} />
        )}
        <DetailRow
          label="Statut du paiement"
          value={
            booking.status?.label?.toLowerCase() === "confirmed"
              ? "Payé"
              : booking.paymentMethod === "in_place"
                ? "Encaissé par le partenaire"
                : "En attente"
          }
        />
      </DetailSection>

      {booking.createdAt && (
        <p className="text-xs text-muted-foreground">
          Réservé le{" "}
          {new Intl.DateTimeFormat("fr-MA", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }).format(new Date(booking.createdAt))}
        </p>
      )}
    </div>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-muted/60 p-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon?: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        {Icon && <Icon className="size-4 text-primary" />}
        {label}
      </p>
      <p
        className={
          accent
            ? "text-right text-sm font-bold text-[#239b4a]"
            : "text-right text-sm font-semibold"
        }
      >
        {value}
      </p>
    </div>
  );
}
